import * as moment from "moment";
import * as React from "react";
import ReactTooltip from "react-tooltip";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Glyphicon from "source/app/presentation/Shared/Glyphicon/Glyphicon";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import { useIntervalToRerender } from "source/app/services/Shared/Hooks/useIntervalToRerender";
import { GetWeatherRowStyleClass } from "./GetWeatherRowStyleClass";
import classNames from "classnames";
import MotwLogicServer from "source/app/services/External/LogicServer/MotwLogicServer";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";
import { useFontSwitcher } from "source/app/presentation/Shared/Fonts/Hooks/useFontSwitcher";

export function PirateWeather() {
    const [state, setState] = React.useState<IWeatherDataEntity[] | undefined>(
        undefined
    );
    const useAnopenscrollFont = useFontSwitcher();

    const dataLoadDateTime = useIntervalToRerender(15 * 60 * 1000); // every 15 minutes

    React.useEffect(() => {
        MotwLogicServer.GetPirateWeatherData()
            .then((response) => setState(response))
            .catch((reason) => console.error(reason));
    }, [dataLoadDateTime]);

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, [state]);

    return DrawWeatherRows(state, useAnopenscrollFont);
}

function DrawWeatherRows(
    state: IWeatherDataEntity[] | undefined,
    useAnopenscrollFont: boolean
) {
    useIntervalToRerender(60000);

    if (state === undefined) {
        return null;
    }

    // ChangeWeatherForTesting(state);

    return (
        <div
            style={{
                isolation: `isolate`,
                margin: `18px auto`,
                maxWidth: `632px`,
                position: `relative`,
            }}
        >
            <WeatherFrameCap verticalPosition="top" />
            <WeatherFrameCap verticalPosition="bottom" />
            <div
                style={{
                    background: `linear-gradient(180deg, #b78a3b 0%, #73501a 24%, #2f1e08 56%, #6b4816 82%, #3e290c 100%)`,
                    border: `1px solid #211505`,
                    boxShadow: `inset 0 0 0 1px rgba(220, 175, 87, 0.72), inset 0 0 0 2px rgba(40, 25, 6, 0.92), 0 3px 12px rgba(0, 0, 0, 0.62), 0 0 10px rgba(151, 103, 30, 0.12)`,
                    boxSizing: `border-box`,
                    clipPath: `polygon(10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px), 0 10px)`,
                    padding: `5px`,
                    position: `relative`,
                    zIndex: 1,
                    width: `100%`,
                }}
            >
                <div
                    style={{
                        border: `1px solid rgba(181, 137, 56, 0.78)`,
                        boxShadow: `inset 0 0 0 1px rgba(24, 15, 4, 0.92)`,
                        clipPath: `polygon(6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px), 0 6px)`,
                        textAlign: `center`,
                        display: `grid`,
                        gridTemplateColumns: `1fr`,
                        margin: `0px auto`,
                        maxWidth: "none",
                        overflow: `hidden`,
                    }}
                >
                    {state.map((record: IWeatherDataEntity) => {
                        return (
                            <div
                                className={GetWeatherRowStyleClass(
                                    record,
                                    useAnopenscrollFont
                                )}
                                key={record.time}
                            >
                                {DisplayTime(record)}
                                <div
                                    className={classNames({
                                        [warningWeatherStyleClass]:
                                            record.apparentTemperature <= -15 ||
                                            record.apparentTemperature >= 30,
                                        [warmWeatherStyleClass]:
                                            record.apparentTemperature >= 25 &&
                                            record.apparentTemperature < 30,
                                    })}
                                    data-tip={GetClothingGuide(
                                        Math.ceil(record.apparentTemperature)
                                    )}
                                >
                                    <GetCellText text="fl" opacity={0.2} />
                                    {Math.ceil(record.apparentTemperature)}
                                    <GetCellText text="C" opacity={0.2} />
                                </div>
                                <DisplayWeatherSummary text={record.summary} />
                                {DisplayWindSpeed(record)}
                                <div
                                    style={{
                                        opacity:
                                            record.precipProbability <= 0.1
                                                ? 0.3
                                                : 1,
                                    }}
                                >
                                    <GetCellText text="pop" />
                                    {Math.ceil(record.precipProbability * 100)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function WeatherFrameCap({
    verticalPosition,
}: {
    verticalPosition: "top" | "bottom";
}) {
    const isTop = verticalPosition === "top";

    return (
        <span
            style={{
                background: `linear-gradient(135deg, #c49a4b 0%, #8b611e 30%, #4c300b 66%, #261806 100%)`,
                border: `1px solid #241707`,
                boxShadow: `0 0 0 1px rgba(193, 148, 67, 0.3), 0 1px 3px rgba(0, 0, 0, 0.72)`,
                height: `14px`,
                left: `50%`,
                position: `absolute`,
                top: isTop ? `-7px` : undefined,
                bottom: isTop ? undefined : `-7px`,
                transform: `translateX(-50%) rotate(45deg)`,
                width: `14px`,
                zIndex: 2,
            }}
        >
            <span
                style={{
                    background: `#1d1304`,
                    border: `1px solid rgba(205, 165, 85, 0.7)`,
                    height: `5px`,
                    left: `3px`,
                    position: `absolute`,
                    top: `3px`,
                    width: `5px`,
                }}
            />
        </span>
    );
}

function DisplayWeatherSummary({ text }: { text: string }) {
    const isBadWeather =
        text.includes("Mostly Cloudy") ||
        text.includes("Overcast") ||
        text.includes("Drizzle") ||
        text.includes("Rain") ||
        text.includes("Snow") ||
        text.includes("Smoke");

    const isGoodWeather = text.includes("Clear");

    const styleClass = StyleClassAuto({
        color: isBadWeather
            ? `#ff9279`
            : isGoodWeather
            ? `yellowgreen`
            : `inherit`,
        fontWeight: isBadWeather || isGoodWeather ? `bold` : `inherit`,
        letterSpacing: `0.08em`,
        textTransform: `uppercase`,
        textShadow: isBadWeather
            ? `0 1px 0 rgba(58, 18, 24, 0.95), 0 0 3px rgba(30, 21, 38, 0.85)`
            : `inherit`,
    });

    return <div className={styleClass}>{text}</div>;
}

function GetClothingGuide(temperature: number) {
    const guide: string[] = [];

    if (temperature >= 30) {
        guide.push(`take water bottle`);
    }

    if (temperature >= 25) {
        guide.push(`wear shorts`);
    }

    if (temperature <= -5) {
        guide.push(`wear heavy winter jacket`);
    } else if (temperature <= 5) {
        guide.push(`thick jacket`);
    } else if (temperature <= 20) {
        guide.push(`light jacket`);
    }

    if (temperature <= 5) {
        guide.push(`take light gloves`);
    }

    if (temperature <= 0) {
        guide.push(`take veil`);
    }

    return `clothing guide: ${
        guide.length > 0 ? guide.join(`, `) : `no special recommendation`
    }`;
}

function ChangeWeatherForTesting(state: IWeatherDataEntity[]) {
    state[0].temperature = -25;
    state[0].windSpeed = 35;
    state[0].precipProbability = 0;
    state[0].icon = `partly-cloudy-night`;
}

function DisplayWindSpeed(record: IWeatherDataEntity) {
    const tooltip = `direction - ${GetCompassDirection(
        record.windBearing
    )} (${Math.floor(record.windBearing)}°)`;

    return (
        <div
            className={classNames({
                [warningWeatherStyleClass]: record.windSpeed >= 20,
            })}
            style={{
                opacity: record.windSpeed <= 10 ? 0.3 : 1,
            }}
            data-tip={tooltip}
        >
            {Math.ceil(record.windSpeed)}
            <ShowWindArrow {...{ record }} />
            <GetCellText text="kmh" opacity={0.2} />
        </div>
    );
}

function ShowWindArrow({ record }: { record: IWeatherDataEntity }) {
    return (
        <span
            className={StyleClass(`showWindArrow_${record.windBearing}`, {
                transform: `rotate(${record.windBearing}deg)`,
                display: `inline-block`,
                opacity: 0.7,
            })}
        >
            <Glyphicon icon={EGlyphicon["arrow-up"]} />
        </span>
    );
}

function GetCompassDirection(angleInDegrees: number) {
    var val = Math.floor(angleInDegrees / 22.5 + 0.5);

    var arr = [
        "N",
        "NNE",
        "NE",
        "ENE",
        "E",
        "ESE",
        "SE",
        "SSE",
        "S",
        "SSW",
        "SW",
        "WSW",
        "W",
        "WNW",
        "NW",
        "NNW",
    ];

    return arr[val % 16];
}

function DisplayTime(record: IWeatherDataEntity) {
    const timeMoment = moment.unix(record.time);
    const day = timeMoment.format(`ddd`).toUpperCase();
    const time = timeMoment.format(`hA`).toUpperCase();

    return (
        <div
            style={{
                letterSpacing: `normal`,
                paddingRight: `14px`,
                position: `relative`,
                textTransform: `none`,
            }}
        >
            <span style={{ opacity: 0.55 }}>{day}</span>
            {` ${time}`}
        </div>
    );
}

export function IsWalkableWeather(record: IWeatherDataEntity) {
    return (
        record.windSpeed <= 10 && record.icon.toLowerCase().includes(`clear`)
    );
}

function GetCellText({
    text,
    opacity = 0.6,
}: {
    text: string;
    opacity?: number;
}) {
    return (
        <span
            className={StyleClass(`WeatherCellText_${opacity}`, {
                opacity,
            })}
        >
            {text}
        </span>
    );
}

const warningWeatherStyleClass = StyleClass(`warningWeatherText`, {
    color: "#ff9279",
    borderRadius: "5px",
    fontWeight: "bold",
    textShadow:
        "0 1px 0 rgba(58, 18, 24, 0.95), 0 0 3px rgba(30, 21, 38, 0.85)",
});

const warmWeatherStyleClass = StyleClass(`warmWeatherText`, {
    color: "#f2d19f",
    fontWeight: "bold",
    textShadow:
        "0 1px 0 rgba(88, 47, 11, 0.95), 0 0 3px rgba(30, 21, 38, 0.85)",
});
